<template>
  <div class="container-fluid" id="cover-cost">
    <Navigation></Navigation>
    <div class="row wrapper">
      <div class="col-12 col-md-4 side-banner">
        <div class="header">
          <!-- <div id="steps" class="steps d-print-none" tabindex="0" role="document">
            <span>Step {{ step }}</span> of {{ step }}
          </div> -->
          <div class="logos">
            <img :src="require(`@/assets/img/${pageContent.companyLogo}`)" class="d-print-block d-none logo-img" alt="">
          </div>
          <h1 tabindex="0" class="print-page-title">Cover costs</h1>
          <div class="hero-image" alt="Income and savings"></div>
        </div>
      </div>
      <div class="col-12 col-md-8 main-banner">
        <div v-if="prerequisitesSet" class="main">
          <div class="d-print-none">
            <h2>Calculate the cost of cover</h2>
            <p>
              <span v-if="skipCalculations">Enter the relevant cover amounts to estimate the cost of your cover.</span>
              <span v-else>Below you can tailor cover amounts to estimate the cost of your cover.</span>
              When cover amounts are measured in units, they’re rounded up to the nearest age-based unit value.
            </p>

            <p>{{pageContent.pages.costs.customIntroText}}</p>

            <p class="cost-member-type" v-if="pageContent.pages.costs.memberType"><b class="member-type-title">{{pageContent.pages.costs.memberType}}:</b> {{ this.data.stepData.memberType }}</p>
            <p v-if="data.stepData.memberType == this.Enum.memberType.PERSONAL && !this.data.stepData.occupationHidden"><b class="occupation-classification-title">{{pageContent.occupationClassificationTerm}}:</b> {{ this.data.stepData.occupation }}</p>
          </div>

          <!-- Print only -->

          <div class="d-none d-print-block">
            <h2>Your inputs</h2>
            <table class="table table-striped">
              <tbody>
                <tr>
                  <td class="first-column">Age</td>
                  <td>{{this.data.stepData.age}}</td>
                </tr>
                <tr>
                  <td>Your sex at birth</td>
                  <td>{{this.data.stepData.gender[0].toUpperCase() + this.data.stepData.gender.slice(1)}}</td>
                </tr>
                <tr>
                  <td>Salary</td>
                  <td>${{ formatCurrency(this.salary) }}</td>
                </tr>
                <tr v-if="pageContent.pages.costs.memberType">
                  <td><span class="member-type-title">Member type</span></td>
                  <td>{{this.data.stepData.memberType}}</td>
                </tr>
                <tr v-if="data.stepData.memberType == this.Enum.memberType.PERSONAL && !this.data.stepData.occupationHidden">
                  <td><span class="occupation-classification-title">{{pageContent.occupationClassificationTerm}}</span></td>
                  <td>{{this.data.stepData.occupation}}</td>
                </tr>
              </tbody>
            </table>
            <h2>Your final estimate</h2>
            <p>Your final estimated {{pageContent.costTerm}} should be used as a guide only.</p>
            <p>This quote is generated on <strong>{{ new Date().toDateString() }}</strong>.</p>
          </div>

          <div class="d-none d-print-block print-result mt-4 mb-5">
            <table class="table table-striped">
              <tbody>
                <tr :class="this.dollarAmountDeathOnly == '0.00' ? 'd-none' : ''">
                  <th class="first-column">Death cover</th>
                  <td>${{ formatCurrency(this.deathOnlyCoverAmount) }}</td>
                </tr>
                <tr :class="this.deathOnlyCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Cover type</td>
                  <td>
                    <span v-if="deathTPDmetric == 'units'">Units ({{ unitsDeathOnly }})</span>
                    <span v-else>Fixed Cover</span>
                  </td>
                </tr>
                <tr :class="this.deathOnlyCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Estimated monthly {{pageContent.costTerm}}</td>
                  <td>
                    <span v-if="deathTPDmetric == 'units'">${{ formatCurrency(this.unitsAmountDeathOnly) }}</span>
                    <span v-else>${{ formatCurrency(this.dollarAmountDeathOnly) }}</span>
                  </td>
                </tr>
                <tr v-if="!disableTPDInputBasedOnAge" :class="this.TPDCoverAmount == '0.00' ? 'd-none' : ''">
                  <th>Total and Permanent Disablement (TPD) cover</th>
                  <td>${{ formatCurrency(this.TPDCoverAmount) }}</td>
                </tr>
                <tr v-if="!disableTPDInputBasedOnAge" :class="this.TPDCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Cover type</td>
                  <td>
                    <span v-if="deathTPDmetric == 'units'">Units ({{ unitsTPD }})</span>
                    <span v-else>Fixed Cover</span>
                  </td>
                </tr>
                <tr v-if="!disableTPDInputBasedOnAge" :class="this.TPDCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Estimated monthly {{pageContent.costTerm}}</td>
                  <td>
                    <span v-if="deathTPDmetric == 'units'">${{ formatCurrency(this.unitsAmountTPD) }}</span>
                    <span v-else>${{ formatCurrency(this.dollarAmountTPD) }}</span>
                  </td>
                </tr>
                <tr v-if="!disableIPInputBasedOnAge" :class="this.IPCoverAmount == '0.00' ? 'd-none' : ''">
                  <th>Income Protection cover</th>
                  <td>${{ formatCurrency(this.IPCoverAmount) }}</td>
                </tr>
                <tr v-if="!disableIPInputBasedOnAge && !this.data.stepData.IPmetricFixed" :class="this.IPCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Units</td>
                  <td>
                    {{ unitsIP }}
                  </td>
                </tr>
                <tr v-if="!disableIPInputBasedOnAge" :class="this.IPCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Benefit period</td>
                  <td>
                    {{ this.benefitPeriod }} Years
                  </td>
                </tr>
                <tr v-if="!disableIPInputBasedOnAge" :class="this.IPCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Waiting period</td>
                  <td>
                    {{ this.waitingPeriod }} Days
                  </td>
                </tr>
                <tr v-if="!disableIPInputBasedOnAge" :class="this.IPCoverAmount == '0.00' ? 'd-none' : ''">
                  <td>Estimated monthly {{pageContent.costTerm}}</td>
                  <td>
                    <span>${{ formatCurrency(this.unitsAmountIP) }}</span>
                  </td>
                </tr>
                <tr v-if="calculatedPremium != '0.00'">
                  <th>Total estimated {{pageContent.costTerm}} per {{ this.estimatedPremiumPeriod }}</th>
                  <td>
                    <span><b>${{ formatCurrency(calculatedPremium) }}</b></span>
                  </td>
                </tr>
                <tr v-else>
                  <th>There are no estimates available.</th>
                </tr>

              </tbody>
            </table>
          </div>
          <!-- end print -->

          <ValidationObserver ref="form">
            <form @submit.prevent="onSubmit" @keydown.enter="$event.preventDefault()">

              <!-- Death cover -->
              <fieldset class="row no-gutters mt-4 d-print-none">
                <label>
                  <img :src="require(`@/assets/img/${pageContent.coverPageIcons.death}`)" alt="" />
                  <b>Death cover amount</b>
                  <b-button id="tooltip-death-cover" variant="tooltip" aria-label="tooltip death cover"><i class="icon-help mb-0"></i></b-button>
                  <b-tooltip target="tooltip-death-cover" placement="top">
                    <span v-html="deathCoverTooltipText"></span>
                  </b-tooltip>
                  <br />
                </label>
                <div v-if="!this.data.stepData.deathTPDMetricFixed" class="d-flex cover-metrics col-xl-6 col-lg-12 ml-4 pl-2 mt-3 d-print-none">
                  <b-button tabindex="0" role="button" class="d-inline-block" :class="{active: deathTPDmetric == 'units'}" @click="toggleUnitsFixed('units'); runCalculation()">{{ pageContent.pages.coverCostLabels.deathCoverUnits }}</b-button>
                  <b-button tabindex="0" role="button" class="d-inline-block" :class="{active: deathTPDmetric == 'fixed'}" @click="toggleUnitsFixed('fixed'); runCalculation()">Fixed Cover</b-button>
                </div>
              </fieldset>

              <fieldset class="row no-gutters mt-4 d-print-none ml-4 pl-2">
                <ValidationProvider class="col-xl-3 col-lg-4 col-5" mode="lazy" name="deathCover" v-slot="{ errors }">
                  <money
                    ref="deathOnlyCoverAmount"
                    data-analytics-capture-value="true"
                    class="form-control"
                    id="deathOnlyCoverAmount"
                    name="deathOnlyCoverAmount"
                    tabindex="0"
                    aria-label="Death cover"
                    v-model.number="deathOnlyCoverAmount"
                    @blur="triggerHeightCheck(); runCalculation()"
                    @keydown.native.enter.prevent="$refs.TPDCoverAmount.$el.focus()">
                  </money>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                </ValidationProvider>

                <div class="col-xl-3 col-lg-6 col-6 d-flex unit estimate pl-2 d-print-none">
                  <div v-if="deathTPDmetric == 'units'">{{this.unitsAmountDeathOnly == 0 ? '0' : this.unitsDeathOnly }} units (1 unit = ${{ formatCurrency(this.deathOnlyUnitSize) }})</div>
                </div>
                <div class="col-xl-6 col-lg-12 estimate">
                  <span>Estimated monthly {{pageContent.costTerm}}:</span>
                  <span v-if="deathTPDmetric == 'units'">${{ formatCurrency(this.unitsAmountDeathOnly) }}</span>
                  <span v-else-if="deathTPDmetric == 'fixed'">${{ formatCurrency(this.dollarAmountDeathOnly) }}</span>
                </div>
              </fieldset>

              <hr class="d-print-none" />

              <!-- TPD -->
              <fieldset class="row no-gutters mt-4 d-print-none">
                <label>
                  <img :src="require(`@/assets/img/${pageContent.coverPageIcons.tpd}`)" alt="" />
                  <b>TPD cover amount</b>
                  <b-button id="tooltip-tpd-cover" variant="tooltip" aria-label="tooltip TPD cover"><i class="icon-help mb-0"></i></b-button>
                  <b-tooltip target="tooltip-tpd-cover" placement="top">
                    <span v-html="TPDCoverTooltipText"></span>
                  </b-tooltip>
                  <br />
                </label>
                <div v-if="!this.data.stepData.deathTPDMetricFixed" class="d-flex cover-metrics col-xl-6 col-lg-12 ml-4 pl-2 mt-3 d-print-none" :class="disableTPDInputBasedOnAge ? 'greyedOut' : ''">
                  <b-button tabindex="0" role="button" class="d-inline-block" :disabled="disableTPDInputBasedOnAge" :class="{active: deathTPDmetric == 'units'}" @click="toggleUnitsFixed('units'); runCalculation()">{{ pageContent.pages.coverCostLabels.TPDCoverUnits }}</b-button>
                  <b-button tabindex="0" role="button" class="d-inline-block" :disabled="disableTPDInputBasedOnAge" :class="{active: deathTPDmetric == 'fixed'}" @click="toggleUnitsFixed('fixed'); runCalculation()">Fixed Cover</b-button>
                </div>
              </fieldset>

              <fieldset class="row no-gutters mt-4 d-print-none ml-4 pl-2">
                <ValidationProvider class="col-xl-3 col-lg-4 col-5" immediate mode="lazy" name="TPDCover"
                  :rules="'required:'+TPDCoverError+'|between:'+config.fields.TPDCover.min+','+TPDCoverMax+','+TPDCoverError" v-slot="{ errors }">
                  <money
                    ref="TPDCoverAmount"
                    data-analytics-capture-value="true"
                    class="form-control"
                    id="TPDCoverAmount"
                    name="TPDCoverAmount"
                    v-model.number="TPDCoverAmount"
                    @blur="triggerHeightCheck(); runCalculation()"
                    tabindex="0"
                    aria-label="TPD cover"
                    :disabled="disableTPDInputBasedOnAge"
                    @keydown.native.enter.prevent="$refs.IPCoverAmount.$el.focus()">
                  </money>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                </ValidationProvider>

                <div class="col-xl-3 col-lg-6 col-6 d-flex unit estimate pl-2 d-print-none">
                  <div v-if="deathTPDmetric == 'units'">{{this.unitsAmountTPD == 0 ? '0' : this.unitsTPD }} units (1 unit = ${{ formatCurrency(this.TPDUnitSize) }})</div>
                </div>
                <div class="col-xl-6 col-lg-12 estimate">
                  <span>Estimated monthly {{pageContent.costTerm}}:</span>
                  <span v-if="deathTPDmetric == 'units'">${{ formatCurrency(this.unitsAmountTPD) }}</span>
                  <span v-else-if="deathTPDmetric == 'fixed'">${{ formatCurrency(this.dollarAmountTPD) }}</span>
                </div>

                <span v-if="disableTPDInputBasedOnAge" role="alert" class="form-text text-error d-print-none">
                  TPD cover is available up to {{ this.data.stepData.TPDMaxAge ?? 69 }} years of age.
                </span>
              </fieldset>

              <hr class="d-print-none" />

              <!-- Income protection -->
              <fieldset class="row no-gutters mt-4 d-print-none">
                <label>
                  <img :src="require(`@/assets/img/${pageContent.coverPageIcons.ip}`)" alt="" />
                  <b>{{ pageContent.pages.costs.incomeProtectionCoverTitle }}</b>
                  <b-button id="tooltip-ip-cover" variant="tooltip" aria-label="tooltip IP cover"><i class="icon-help mb-0"></i></b-button>
                  <b-tooltip target="tooltip-ip-cover" placement="top">
                    <span v-html="pageContent.pages.income.IPCoverTooltipText"></span>
                  </b-tooltip>
                  <br />
                </label>
              </fieldset>
              <fieldset class="row no-gutters mt-4 d-print-none ml-4 pl-2">
                <ValidationProvider class="col-xl-3 col-lg-4 col-5" immediate mode="lazy" name="IPCover" :rules="'optionalBetween:'+config.fields.IPCover.min+','+IPCoverMax+','+IPCoverError" v-slot="{ errors }">
                  <money
                    ref="IPCoverAmount"
                    data-analytics-capture-value="true"
                    class="form-control"
                    id="IPCoverAmount"
                    name="IPCoverAmount"
                    v-model="IPCoverAmount"
                    v-on:blur="triggerHeightCheck(); runCalculation()"
                    tabindex="0"
                    aria-label="Income Protection cover"
                    :disabled="disableIPInputBasedOnAge"
                    @keydown.native.enter.prevent="$refs.benefitPeriod.focus()">
                  </money>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                </ValidationProvider>
                <div class="col-xl-3 col-lg-6 col-6 d-flex unit estimate pl-2 d-print-none justify-content-start">
                  <div v-if="!this.data.stepData.IPmetricFixed">
                    {{ this.unitsAmountIP == 0 ? '0' : this.unitsIP }} units (1 unit = ${{ formatCurrency(this.IPUnitSize/52) }} per week)
                  </div>
                </div>
              </fieldset>

              <fieldset class="row no-gutters mt-4 d-print-none ml-4 pl-2 flex-column">
                <div class="d-flex income-protection-wrapper d-print-none pl-0">
                  <div class="income-protection-select-wrapper d-flex col-12 col-xl-6 pl-0">
                    <div class="col-xl-6 col-lg-6 col-12 d-flex flex-column px-0 income-protection-select">
                      <label><b>Benefit period</b></label>
                      <select ref="benefitPeriod" id="" class="form-control" aria-label="Default select example" tabindex="0"
                        v-model="benefitPeriod" :disabled="disableIPInputBasedOnAge" @change="triggerHeightCheck(); runCalculation()"
                        @keydown.enter.prevent="$refs.waitingPeriod.focus()">
                        <option value="2">2 Years</option>
                        <option value="5" v-if="benefitPeriodElegibility5">5 Years</option>
                        <option value="65" v-if="benefitPeriodElegibility65">To Age 65</option>
                      </select>
                    </div>
                    <div class="col-xl-6 col-lg-6 col-12 d-flex flex-column px-0 income-protection-select">
                      <label><b>Waiting period</b></label>
                      <select ref="waitingPeriod" id="" class="form-control" aria-label="Default select example" tabindex="0"
                        v-model="waitingPeriod" :disabled="disableIPInputBasedOnAge" @change="triggerHeightCheck(); runCalculation()"
                        @keydown.enter.prevent="$refs.estimatedPremiumPeriod.focus()">
                        <option value="30">30 Days</option>
                        <option value="60">60 Days</option>
                        <option value="90">90 Days</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-xl-6 col-12 estimate d-print-none justify-content-end align-self-end pr-0">
                    <span>Estimated monthly {{pageContent.costTerm}}:</span><span>${{ formatCurrency(this.unitsAmountIP) }}</span>
                  </div>
                </div>

                <span v-if="disableIPInputBasedOnAge" role="alert" class="form-text text-error d-print-none">
                  Income Protection cover is available up to {{ this.data.stepData.IPMaxAge ?? 66 }} years of age.
                </span>
              </fieldset>

              <hr  class="d-print-none" />

              <div class="results d-print-none">
                <span class="col-6 p-0">Estimated total {{pageContent.costTerm}}:</span>
                <div class="total-wrapper col-xl-6 col-12">
                  <span class="col-6 p-0 text-right">${{ formatCurrency(calculatedPremium) }}</span>
                  <span class="col-6 pr-0">
                    <select ref="estimatedPremiumPeriod" id="salaryPeriod" class="form-control col-4" aria-label="Default select example" v-model="estimatedPremiumPeriod" tabindex="0" @change="triggerHeightCheck(); runCalculation()">
                      <option value="week">Weekly</option>
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </span>
                </div>
              </div>
              <p class="m-4">
                <span v-html="pageContent.pages.costs.outroText"></span>
              </p>

              <div class="d-none d-print-block print-result mt-4 mb-5 pagebreak">
                <Results class="d-none d-print-block" />
              </div>

              <button v-if="!skipCalculations" type="button" class="reset-assumptions-button mb-2 d-print-none p-0" @click="resetCover(); runCalculation();">
                <i class="icon-undo"></i>Reset Cover
              </button>
              <br/>
              <router-link class="assumption-button d-print-none" :to="{ name: 'Disclaimer', params: {activeTab: 'disclaimer' } }" tabindex="0" role="button"><i class="icon-info disclaimer"></i>Disclaimer</router-link>

              <div class="nav-button row d-print-none m-0">
                <button :disabled="!isValid" class="print-download-button col" type="button" onclick="window.print();">
                  <i class="icon-print"> </i>Print and Download PDF
                </button>
                <router-link class="back-button col" :to="backDestination" tabindex="0" role="button">Back</router-link>
                <!-- <router-link class="next-button col" to="/next" tabindex="0" role="button">Next</router-link> -->
                <button class="next-button col" v-bind:class="[isValid ? 'is-active' : 'is-disabled']" type="submit" tabindex="0" role="button">Next</button>
              </div>
            </form>
          </ValidationObserver>
        </div>
      </div>

    </div>
    <CustomFooter></CustomFooter>
  </div>
</template>


<script>
import { mapGetters } from 'vuex';
import { mapFields } from "vuex-map-fields";
import Money from '../components/Money';

import { ValidationProvider, extend, ValidationObserver } from 'vee-validate';
import { required, between } from 'vee-validate/dist/rules';
import CustomFooter from '../components/CustomFooter';
import Navigation from "../components/Navigation.vue";
import Results from '../views/Results.vue';

import _ from 'lodash';

extend('required', {
  ...required,
  params: ['msg'],
  message: '{msg}'
});

extend('between', {
  ...between,
  params: ['min', 'max', 'msg'],
  validate(value, { min, max }) {
    return value >= min && value <= max;
  },
  message: '{msg}'
});

extend('optionalBetween', {
  ...between,
  params: ['min', 'max', 'msg'],
  validate(value, { min, max }) {
    return value ? value >= min && value <= max : true;
  },
  message: '{msg}'
});

export default {
  name: 'CoverCost',
  components: { Money, ValidationProvider, ValidationObserver, CustomFooter, Results, Navigation },
  data() {
		return {
			deathTPDmetric: 'units',
      estimatedPremium: 0,
      calculatedPremium: 0,
      estimatedPremiumPeriod: 'month',
      unitsAmountDeathOnly: Number(0).toFixed(2),
      unitsAmountTPD: Number(0).toFixed(2),
      unitsDeathOnly: null,
      unitsTPD: null,
      unitsIP: null,
      deathOnlyUnitSize: 0,
      TPDUnitSize: 0,
      IPUnitSize: 5200,
      dollarAmountTPD: Number(0).toFixed(2),
      dollarAmountDeathOnly: Number(0).toFixed(2),
      unitsAmountIP: Number(0).toFixed(2),
      disableIPInputBasedOnAge: null,
      disableTPDInputBasedOnAge: null,
      needsDeathOnlyCoverUnitsAmount: 0,
      needsTPDCoverUnitsAmount: 0
		};
	},
  computed: {
    ...mapFields([
      "data",
      "assets",
      "needsDeathOnlyCoverAmount",
      "deathOnlyCoverAmount",
      "needsTPDCoverAmount",
      "TPDCoverAmount",
      "needsIPCoverAmount",
      "IPCoverAmount",
      "gender",
      "coverAmount",
      "benefitPeriod",
      "waitingPeriod",
      "protectionBenefits",
      "superBenefits",
      "unitsOfCover",
      "costPerPeriod",
      "costPerYear",
      "skipCalculations",
      "prevTab",
      "constants"
    ]),
    ...mapGetters(["Enum", "config", "formatOptions", "pageContent", "premiumVariables", "api", "calculatedPremiums", "benefitPeriodElegibility5", "benefitPeriodElegibility65"]),
    backDestination() {
			return this.data.stepData.memberType === this.Enum.memberType.PERSONAL && !this.data.stepData.occupationHidden ? "/occupation" : "/next-options";
		},
    prerequisitesSet() {
      return this.data.stepData.age && this.data.stepData.gender && this.data.stepData.memberType && this.salary;
    },
    salary() {
      return this.data.stepData.salaryPeriod === "Month" ? this.data.stepData.salaryMonthly * 12 :
        this.data.stepData.salaryPeriod === "Week" ? this.data.stepData.salaryWeekly * this.constants.WEEKS_PER_YEAR :
        this.data.stepData.salaryYearly;
    },
    IPCoverMaxSalaryPercent() {
      return this.data.stepData.IPCoverMaxSalaryPercent ? this.data.stepData.IPCoverMaxSalaryPercent/100 : 0.85;
    },
    IPCoverSalaryMax() {
      return Math.floor(this.salary * this.IPCoverMaxSalaryPercent);
    },
    IPCoverMax() {
      const ipCoverMax = Math.min(this.IPCoverSalaryMax, this.config.fields.IPCover.max);
      const ipCoverMaxAnnual = this.data.stepData.IPmetricFixed ? ipCoverMax : Math.ceil(ipCoverMax / this.IPUnitSize) * this.IPUnitSize;
      return this.data.stepData.IPCoverMonthly ? Math.floor(ipCoverMaxAnnual / 12) : ipCoverMaxAnnual;
    },
    IPCoverError() {
      return this.pageContent.pages.income.IPCoverError + this.formatCurrency(this.IPCoverMax) + (this.data.stepData.IPCoverMonthly ? "" : " p.a") +
      (this.IPCoverSalaryMax < this.config.fields.IPCover.max ? " ("+this.IPCoverMaxSalaryPercent*100+"% of salary)." : ".");
    },
    TPDMaxUnits() {
      return this.data.stepData.TPDMaxUnits;
    },
    isMetricUnitsAndTPDMaxUnits() {
      return this.TPDMaxUnits && this.deathTPDmetric == 'units';
    },
    TPDExceedsDeathCoverMaxAge() {
      return this.data.stepData.TPDExceedsDeathCoverMaxAge;
    },
    TPDUnitsCoverMax() {
      return this.TPDUnitSize * this.TPDMaxUnits;
    },
    TPDCoverMax() {
      const TPDCoverMax = this.deathTPDmetric == 'units' && this.TPDMaxUnits ?
        this.TPDUnitsCoverMax : this.config.fields.TPDCover.max;
      return this.TPDExceedsDeathCoverMaxAge &&
        ((this.data.stepData.age > this.TPDExceedsDeathCoverMaxAge) ||
        (this.data.stepData.age <= this.TPDExceedsDeathCoverMaxAge && this.deathTPDmetric == 'fixed') ||
        !this.deathOnlyCoverAmount) ?
        Math.min(this.deathOnlyCoverAmount, TPDCoverMax) : TPDCoverMax;
    },
    TPDCoverError() {
      const TPDCoverMax = this.TPDCoverMax;
      return TPDCoverMax ? this.pageContent.pages.income.TPDCoverError + this.formatCurrency(TPDCoverMax) +
        (this.isMetricUnitsAndTPDMaxUnits && TPDCoverMax == this.TPDUnitsCoverMax ?
        " ("+this.TPDMaxUnits+" units)." : ".") : "TPD Cover also requires a Death Cover amount.";
    },
    deathCoverTooltipText() {
      return this.pageContent.pages.income.deathCoverTooltipText ?? this.pageContent.pages.income.deathTPDCoverTooltipText;
    },
    TPDCoverTooltipText() {
      return this.pageContent.pages.income.TPDCoverTooltipText ?? this.pageContent.pages.income.deathTPDCoverTooltipText;
    },
    step() {
      return this.skipCalculations ? 2 : 7;
    },
    isValid() {
      const TPDCoverAmountValid = this.TPDCoverAmount <= this.TPDCoverMax;
      const IPCoverAmountValid = !this.IPCoverAmount || (this.IPCoverAmount >= this.config.fields.IPCover.min && this.IPCoverAmount <= this.IPCoverMax);
      return TPDCoverAmountValid && IPCoverAmountValid;
    }
  },
  methods: {
    roundHalfUp2(number) {
      return Number(Math.round(number+'e'+2)+'e-'+2).toFixed(2);
    },
    formatCurrency: (value) => {
      return value === null ? "" : value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    resetCover() {
      this.deathOnlyCoverAmount = this.needsDeathOnlyCoverAmount;
      this.TPDCoverAmount = this.needsTPDCoverAmount;
      this.IPCoverAmount = Math.min(this.needsIPCoverAmount, this.IPCoverMax);
    },
    triggerHeightCheck: function() {
      // Add slight delay otherwise incorrect height is read
      var rf = this;
      setTimeout(function() {
        rf.$refreshIframeHeight();
      }, 300);
    },
    onSubmit () {
      this.$refs.form.validateWithInfo().then(result => {
        if (!result.isValid) {
          var fieldArr = _.valuesIn(result.fields);
          var firstError = document.getElementById(fieldArr[0].id)
          if(firstError) {
            firstError.scrollIntoView({behavior: 'smooth'});
          }
          this.triggerHeightCheck();
          return;
        }
        this.$router.push('/next');
      });
    },
    toggleUnitsFixed(coverType) {
      this.deathTPDmetric = coverType;
      if (coverType == 'fixed') {
        if (this.deathOnlyCoverAmount == this.needsDeathOnlyCoverUnitsAmount) {
          this.deathOnlyCoverAmount = this.needsDeathOnlyCoverAmount;
        }
        if (this.TPDCoverAmount == this.needsTPDCoverUnitsAmount) {
          this.TPDCoverAmount = this.needsTPDCoverAmount;
        }
      }
    },
    runCalculation() {
      this.calculatedPremiums(this);
      this.sendAnalytics();
      this.triggerHeightCheck();
    },
    sendAnalytics() {
      if(this.isValid && this.estimatedPremium) {
        const stepData = this.data.stepData;
        const memberType = stepData.memberType == this.Enum.memberType.EMPLOYER ? "Employer" :
          stepData.memberType == this.Enum.memberType.PERSONAL ? "Personal" : "Merged";

        let occupation;
        if(stepData.memberType == this.Enum.memberType.PERSONAL) {
          occupation = stepData.occupation == this.Enum.occupation.PROFESSIONAL ? "Professional" :
            stepData.occupation == this.Enum.occupation.WHITE ? "White" :
            stepData.occupation == this.Enum.occupation.LIGHT ? "Light" :
            stepData.occupation == this.Enum.occupation.MANUAL ? "Manual" :
            stepData.occupation == this.Enum.occupation.HEAVY ? "Heavy" : "Special";
        }

        const apiData = {
          id: this.api.userUUID,
          age: stepData.age,
          gender: stepData.gender,
          salary: this.salary,
          quote: {
            memberType,
            occupation,
            deathTpdMetric: this.deathTPDmetric,
            benefitPeriod: this.benefitPeriod,
            waitingPeriod: this.waitingPeriod,
            premiumPeriod: this.estimatedPremiumPeriod,
            quoteDeathCover: this.deathOnlyCoverAmount,
            quoteTpdCover: this.TPDCoverAmount,
            quoteIpCover: this.IPCoverAmount,
            deathUnits: this.unitsDeathOnly,
            deathPremium: this.deathTPDmetric == 'units' ? this.unitsAmountDeathOnly : this.dollarAmountDeathOnly,
            tpdUnits: this.unitsTPD,
            tpdPremium: this.deathTPDmetric == 'units' ? this.unitsAmountTPD : this.dollarAmountTPD,
            ipUnits: this.unitsIP,
            ipPremium: this.unitsAmountIP,
            totalPremium: this.calculatedPremium
          }
        };

        this.api.client.post(this.api.path, apiData).catch(err => (console.log(err)));
      }
    }
  },
  beforeDestroy() {
    if(this.isValid && this.estimatedPremium) {
      this.$gtag.event('step-quote', { event_category: 'field-data', event_label: JSON.stringify({
        "deathCover": this.deathOnlyCoverAmount,
        "TPDCover": this.TPDCoverAmount,
        "IPCover": this.IPCoverAmount,
        "deathTPDmetric": this.deathTPDmetric,
        "benefitPeriod": this.benefitPeriod,
        "waitingPeriod": this.waitingPeriod,
        "estimatedPremiumPeriod": this.estimatedPremiumPeriod
      })});
      this.$gtag.event('step-quote', { event_category: 'result-data', event_label: JSON.stringify({
        "deathTPDmetric": this.deathTPDmetric,
        "deathUnits": this.unitsDeathOnly,
        "deathPremium": this.deathTPDmetric == 'units' ? this.unitsAmountDeathOnly : this.dollarAmountDeathOnly,
        "TPDUnits": this.unitsTPD,
        "TPDPremium": this.deathTPDmetric == 'units' ? this.unitsAmountTPD : this.dollarAmountTPD,
        "IPUnits": this.unitsIP,
        "IPPremium": this.unitsAmountIP,
        "totalPremiumPeriod": this.estimatedPremiumPeriod,
        "totalPremium": this.calculatedPremium
      })});
    }
  },
  mounted() {
    this.triggerHeightCheck();
    this.$scrollIframeTop();
    window.addEventListener("resize", this.$refreshIframeHeight);
    if(!this.data.stepData.memberType) {
      this.data.stepData.memberType = this.Enum.memberType.EMPLOYER;
    }
    if(!this.prerequisitesSet) {
      this.$router.push('/next-options');
    } else {
      if(this.IPCoverAmount > this.IPCoverMax) {
        this.IPCoverAmount = this.IPCoverMax;
      }
      this.deathTPDmetric = this.data.stepData.deathTPDMetricFixed ? 'fixed' : 'units';
      this.runCalculation();
      this.needsDeathOnlyCoverUnitsAmount = this.deathOnlyCoverAmount;
      this.needsTPDCoverUnitsAmount = this.TPDCoverAmount;
    }
  },
  destroyed() {
    this.prevTab = 7
    window.removeEventListener("resize", this.$refreshIframeHeight);
  }
}

</script>
